import { useState } from 'preact/hooks'
import { Settings, FileCheck } from 'lucide-preact'
import type { WebViewDto } from '@/api/webs'
import { TopLevelEditSection } from '../panels/TopLevelEditSection'
import { AssetWorkflowSettings } from '../panels/AssetWorkflowSettings'

interface Props {
  web: WebViewDto
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
  clientCompanyId: string
  workspaceId: string
  campaignId: string
}

type SettingsSection = 'detail' | 'workflow'

interface SettingsNavItem {
  key: SettingsSection
  label: string
  icon: typeof Settings
  description: string
}

const navItems: SettingsNavItem[] = [
  {
    key: 'detail',
    label: 'Web Details',
    icon: Settings,
    description: 'Configure basic web settings'
  },
  {
    key: 'workflow',
    label: 'Asset Workflow',
    icon: FileCheck,
    description: 'Manage asset approval workflow'
  }
]

export function WebSettingsTab({ web, onPatchWeb, clientCompanyId, workspaceId, campaignId }: Props) {
  const [activeSection, setActiveSection] = useState<SettingsSection>('detail')

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Sidebar Navigation - Mobile: Horizontal tabs, Desktop: Vertical sidebar */}
      <nav className="lg:w-56 flex-shrink-0">
        {/* Mobile: Horizontal tabs */}
        <div className="lg:hidden flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = activeSection === item.key
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveSection(item.key)}
                className={`flex-1 flex flex-col items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            )
          })}
        </div>

        {/* Desktop: Vertical sidebar */}
        <div className="hidden lg:block space-y-1">
          <h3 className="px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Settings
          </h3>
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = activeSection === item.key
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveSection(item.key)}
                className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 shadow-sm'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isActive ? 'text-primary-600 dark:text-primary-500' : 'text-slate-500 dark:text-slate-400'}`} />
                <div className="flex-1 text-left">
                  <div className="font-medium">{item.label}</div>
                  <div className={`text-xs mt-0.5 ${isActive ? 'text-primary-600/80 dark:text-primary-400/80' : 'text-slate-500 dark:text-slate-400'}`}>
                    {item.description}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        {activeSection === 'detail' && (
          <TopLevelEditSection
            web={web}
            onPatchWeb={onPatchWeb}
            clientCompanyId={clientCompanyId}
            workspaceId={workspaceId}
            campaignId={campaignId}
          />
        )}

        {activeSection === 'workflow' && (
          <AssetWorkflowSettings
            level="web"
            webId={web.Id}
            workspaceId={workspaceId}
            clientCompanyId={clientCompanyId}
            campaignId={campaignId}
          />
        )}
      </div>
    </div>
  )
}
